/**
 * Canonical pallet-status taxonomy — the SINGLE source for these values.
 * `@stopal/db` builds the `pallet_status` Postgres enum FROM this array
 * (`pgEnum('pallet_status', PALLET_STATUSES)`), so the two can't drift and no
 * lockstep test is needed. This leaf package is the shared home because the
 * frontend can import it but can't import `@stopal/db`.
 */
export const PALLET_STATUSES = [
  'assembling',
  'ready_for_delivery',
  'in_transit_to_user',
  'at_user',
  'in_transit_to_warehouse',
  'stored',
  'in_transit_transfer',
  'returned',
  'decommissioned',
  // Waiting at a scan & go place (`scan_go_place`) for a customer to scan and
  // claim it. It has left our floor and belongs to no customer yet.
  'at_scan_go_place',
] as const

export type PalletStatus = (typeof PALLET_STATUSES)[number]

/**
 * Whether the pallet is between places — on a truck, part-way through a trip.
 *
 * The distinction is load-bearing for anything asking WHERE a pallet is,
 * because an in-transit pallet's location columns lie in two different ways
 * and neither is obvious:
 *
 * - a box collected from a CUSTOMER keeps `current_address_id` — the driver's
 *   advance writes an address only on an `at_user` arrival, so the column
 *   still names the place the box has just LEFT;
 * - a box taken off OUR floor keeps neither column — the load-out nulls
 *   `current_warehouse_id` and it never had an address.
 *
 * So "has no location" is NOT the same question as "is in transit", and code
 * that uses the former as a proxy for the latter silently covers only the
 * second case. The `in_transit` arm of `palletPlace`, whose exhaustive switch
 * is where a new status decides.
 */
export function isInTransit(status: PalletStatus | null | undefined): boolean {
  return palletPlace(status) === 'in_transit'
}

/**
 * The in-transit statuses as a list, for a SQL predicate that cannot call a
 * function. DERIVED from `isInTransit`, so `palletPlace`'s exhaustive switch
 * stays the one place a new status is classified — a hand-listed literal here
 * would be free to disagree with it, and would disagree silently.
 */
export const IN_TRANSIT_PALLET_STATUSES: PalletStatus[] =
  PALLET_STATUSES.filter((status) => isInTransit(status))

/**
 * Whether a pallet with this status has reached the customer — i.e. the
 * driver's delivery stop completed, so a customer name and inventory can
 * exist. A
 * warehouse-assigned pallet already has a barcode before it ships, so barcode
 * presence alone doesn't mean delivered. Exhaustive over `PalletStatus` — a
 * new status fails compilation here instead of silently opening or closing
 * the scan-to-name gate.
 */
export function hasReachedCustomer(
  status: PalletStatus | null | undefined,
): boolean {
  switch (status) {
    case 'at_user':
    case 'in_transit_to_warehouse':
    case 'in_transit_transfer':
    case 'stored':
    case 'returned':
    case 'decommissioned':
      return true
    case 'assembling':
    case 'ready_for_delivery':
    case 'in_transit_to_user':
    // Stocked at a scan & go place but not yet claimed: no customer holds it,
    // so nothing can have been delivered to one.
    case 'at_scan_go_place':
    case null:
    case undefined:
      return false
    default:
      return status satisfies never
  }
}

/**
 * The statuses for which `pallet.current_address_id` names where the box
 * actually IS — the ones that reach the address arm of the Sites page's
 * `bucketOf`, and therefore the only ones a site can be resolved for in SQL.
 *
 * DERIVED from the two exhaustive predicates above, for exactly the reason
 * `IN_TRANSIT_PALLET_STATUSES` is: `bucketOf` asks `isInTransit` first and
 * `hasReachedCustomer` second, so a hand-listed literal here would be free to
 * disagree with either, and would disagree silently. A new status is
 * classified once, in the two switches, and flows here.
 *
 * It does NOT encode the third gate — `current_warehouse_id` outranking the
 * address column — because that is a column test rather than a status test.
 * A caller resolving a site owes it separately; `boxesAtMemberSites` in
 * `apps/api/src/lib/member-site-filter.ts` is the one place that pairing is
 * written.
 */
// `readonly`, because this is read at query-build time inside an
// authorization filter: a `.push()` anywhere would widen every restricted
// member's pallet read. Callers needing drizzle's mutable `inArray` copy it.
export const SITE_ATTRIBUTABLE_PALLET_STATUSES: readonly PalletStatus[] =
  PALLET_STATUSES.filter(
    (status) => !isInTransit(status) && hasReachedCustomer(status),
  )

/**
 * Where a pallet physically IS, as far as its status can say.
 *
 * - `none` — there is no physical box yet (or it is out of service), so there
 *   is nothing to locate.
 * - `warehouse` — on our floor: reserved for a delivery that has not left,
 *   shelved for storage, or back in inventory.
 * - `in_transit` — on a truck, between places. Deliberately NOT attributed to
 *   either end: the columns it carries name the door it just left.
 * - `customer` — standing at the customer's place, which is the ONLY case in
 *   which an address of theirs is the answer.
 */
export const PALLET_PLACES = [
  'none',
  'warehouse',
  'in_transit',
  'customer',
] as const

export type PalletPlace = (typeof PALLET_PLACES)[number]

/**
 * The customer surfaces' rule for "Currently at". It exists because without it
 * both would fall through to the ORDER's address for every status — so a box
 * reserved on our floor would read as "Currently at" the house it was going to,
 * and a stored box would read as standing at the door it had been collected from. The
 * ops dashboard already asked the question this way (warehouse first, the
 * customer's address only once the box is `at_user`); this is that rule,
 * declared once where the list SQL and the detail read both import it.
 *
 * It is the answer AS FAR AS THE STATUS CAN SAY. A carrier move restamps no
 * status, so the detail read overrides `customer` with `in_transit` while a
 * carrier has the box (`resolvePalletPlaces`), and the page renders that
 * server answer rather than calling this.
 *
 * Exhaustive over `PalletStatus` — a new status decides rather than defaults
 * in. `hasReachedCustomer` answers a DIFFERENT question (has the delivery scan
 * happened, which is true of a box on its way to our warehouse) and must not
 * be read as this one.
 */
export function palletPlace(
  status: PalletStatus | null | undefined,
): PalletPlace {
  switch (status) {
    case 'assembling':
    case 'ready_for_delivery':
    case 'stored':
    case 'returned':
      return 'warehouse'
    case 'in_transit_to_user':
    case 'in_transit_to_warehouse':
    case 'in_transit_transfer':
      return 'in_transit'
    case 'at_user':
      return 'customer'
    // Not `warehouse`: it has left our floor, and "Our warehouse" would be
    // false. Not `customer`: nobody has claimed it, so there is no customer
    // whose address answers. A claim moves it straight to `at_user`, so a
    // customer surface has no pallet of theirs in this status to locate.
    case 'at_scan_go_place':
    case 'decommissioned':
    case null:
    case undefined:
      return 'none'
    default:
      return status satisfies never
  }
}

/**
 * The statuses of a box on our floor, and of a box at the customer's, as lists
 * for a SQL `CASE` that cannot call a function. DERIVED from `palletPlace`, for
 * the reason `IN_TRANSIT_PALLET_STATUSES` gives: the exhaustive switch stays
 * the one place a status is classified, and a hand-listed literal here would
 * be free to disagree with it silently.
 */
export const WAREHOUSE_PALLET_STATUSES: PalletStatus[] = PALLET_STATUSES.filter(
  (status) => palletPlace(status) === 'warehouse',
)
export const AT_CUSTOMER_PALLET_STATUSES: PalletStatus[] =
  PALLET_STATUSES.filter((status) => palletPlace(status) === 'customer')

/**
 * A `pallet_status` value as an operator reads it.
 *
 * A MAP now, not a formatter. It was `replace(/_/g, ' ')` while no status
 * needed wording of its own, and its own docblock named the arrival of one as
 * the moment to swap it — `at_scan_go_place` is that arrival: the split-word
 * rendering reads "At Scan Go Place", and an ampersand is not something a word
 * split can produce.
 *
 * Exhaustive over `PalletStatus`, so a status added later fails the build here
 * instead of quietly rendering a snake_case value at a warehouse worker.
 *
 * **Operator vocabulary, deliberately.** What a CUSTOMER reads for the same
 * status comes from the `customer_message` catalogue (`pallet.status.<value>`).
 * `palletStatusPresets` (`apps/web/src/components/domain/status`) is the other
 * operator lexicon for this taxonomy and is what the ops status CHIPS render;
 * the two say the same words on purpose, and
 * `.agents/reference/customer-copy.md` explains why the preset one can only be
 * policed on a rendered page rather than at the defaults level.
 */
const PALLET_STATUS_LABELS: Record<PalletStatus, string> = {
  assembling: 'assembling',
  ready_for_delivery: 'ready for delivery',
  in_transit_to_user: 'in transit to user',
  at_user: 'at user',
  in_transit_to_warehouse: 'in transit to warehouse',
  stored: 'stored',
  in_transit_transfer: 'in transit transfer',
  returned: 'returned',
  decommissioned: 'decommissioned',
  at_scan_go_place: 'at Scan & Go',
}

/**
 * The label, for a value that may not be one this build knows — the two staff
 * surfaces read it off a payload. An unknown value renders as it arrived rather
 * than as a blank, which is what a bare map lookup would give.
 */
export function formatPalletStatus(status: PalletStatus | string): string {
  // A narrowing check rather than a cast: the payload is typed `string` at
  // these call sites, and `as` here would make an unknown value a silent
  // `undefined` lookup instead of reaching the fallback.
  if (Object.hasOwn(PALLET_STATUS_LABELS, status))
    return PALLET_STATUS_LABELS[status as keyof typeof PALLET_STATUS_LABELS]
  return status.replace(/_/g, ' ')
}
